import { Outlet, createFileRoute, redirect, useRouterState } from '@tanstack/react-router';

import { BottomNav } from '@/components/BottomNav';
import { SideNav } from '@/components/SideNav';
import { Toaster } from '@/components/Toaster';
import { TourProvider } from '@/components/tour/TourProvider';
import { Welcome } from '@/components/Welcome';
import { cn } from '@/lib/cn';

/**
 * Layout de la aplicación autenticada.
 *
 * `beforeLoad` corta el paso antes de montar nada: si no hay sesión válida se
 * salta a `/login` guardando en `redirect` la ruta que se pedía, para volver
 * a ella después de entrar.
 */
export const Route = createFileRoute('/_app')({
  beforeLoad: ({ context, location }) => {
    if (context.auth.status !== 'authenticated') {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: AppLayout,
});

/**
 * Páginas que en escritorio reparten su contenido en varias columnas y por eso
 * usan la rejilla ancha (`max-w-wide`). El resto —formularios, fichas,
 * listas sencillas— se queda en una columna de lectura (`max-w-read`): un
 * formulario a 1100 px de ancho no se lee mejor, se lee peor.
 */
const WIDE_ROUTES: ReadonlySet<string> = new Set([
  '/_app/train/',
  '/_app/train/session/$clientId',
  '/_app/food/',
  '/_app/food/recipes/',
  '/_app/progress/',
  '/_app/community/',
  '/_app/exercises/',
]);

function AppLayout() {
  const wide = useRouterState({
    select: (state) => WIDE_ROUTES.has(state.matches[state.matches.length - 1]?.routeId ?? ''),
  });

  return (
    <TourProvider>
      <div className="min-h-dvh bg-surface">
        <SideNav />
        {/* En móvil, la pantalla entera hasta `max-w-tablet` (un móvil en
          horizontal o una ventana estrecha no se quedan con una columna de
          450 px); desde `md`, el hueco de la barra lateral y un ancho según la
          página. */}
        <div className="md:pl-rail lg:pl-sidebar">
          <main
            className={cn(
              'mx-auto w-full max-w-tablet px-4 pt-safe pb-nav xs:px-6 md:px-8 lg:px-10',
              // Móvil con muesca en horizontal: la muesca puede quedar a la derecha.
              'md:pr-[max(2rem,env(safe-area-inset-right,0px))] lg:pr-[max(2.5rem,env(safe-area-inset-right,0px))]',
              wide ? 'md:max-w-wide' : 'md:max-w-read',
            )}
          >
            <Outlet />
          </main>
        </div>
        <BottomNav />
        <Welcome />
        <Toaster />
      </div>
    </TourProvider>
  );
}
